/** Store → collection editor: manual (picked + ordered products) or smart (rules). */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Collapse, Empty, Form, Input, InputNumber, List, Popconfirm, Radio, Result, Row, Select, Space, Spin, Switch, Typography, message } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, ExportOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import RichEditor from './RichEditor';
import CustomFieldsCard from './CustomFieldsCard';
import TranslationsCard from './TranslationsCard';
import { fromMinor, siteUrl, sitePath, slugify, toMinor, toastErr, useSite } from './common';
import { ProductPicker, Thumb, useDragList } from './ShopCommon';

const { Text } = Typography;

const FIELDS = [
    { value: 'title', label: 'Product title' }, { value: 'type', label: 'Product type' }, { value: 'vendor', label: 'Vendor' },
    { value: 'tag', label: 'Product tag' }, { value: 'price', label: 'Price', money: true }, { value: 'compare_at_price', label: 'Compare-at price', money: true },
    { value: 'inventory', label: 'Inventory stock', num: true }, { value: 'variant_title', label: 'Variant title' },
];
const OPS = [
    { value: 'equals', label: 'is equal to' }, { value: 'not_equals', label: 'is not equal to' }, { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' }, { value: 'starts_with', label: 'starts with' }, { value: 'ends_with', label: 'ends with' },
    { value: 'gt', label: 'is greater than', num: true }, { value: 'lt', label: 'is less than', num: true },
];
const SORTS = [
    { value: 'manual', label: 'Manually' }, { value: 'best_selling', label: 'Best selling' }, { value: 'title_asc', label: 'Title A–Z' },
    { value: 'title_desc', label: 'Title Z–A' }, { value: 'price_asc', label: 'Price, low to high' }, { value: 'price_desc', label: 'Price, high to low' },
    { value: 'created_desc', label: 'Newest' }, { value: 'created_asc', label: 'Oldest' },
];
const EMPTY = { title: '', handle: '', description_html: '', image: null, kind: 'manual', rules: { match: 'all', conditions: [] }, sort_order: 'manual', published: true, seo: { title: '', description: '' }, products: [] };

const CollectionEdit = () => {
    const { t } = useTranslation();
    const { cid } = useParams();
    const isNew = !cid;
    const navigate = useNavigate();
    const { api, site, siteId, currency } = useSite();
    const [c, setC] = useState(isNew ? EMPTY : null);
    const [err, setErr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [picking, setPicking] = useState(false);
    const [productsDirty, setProductsDirty] = useState(false);
    const [handleTouched, setHandleTouched] = useState(!isNew);

    useEffect(() => {
        if (isNew) { setC(EMPTY); return undefined; }
        let live = true;
        api.get(`/collections/${cid}`).then((d) => live && setC({ ...EMPTY, ...d, rules: d?.rules?.conditions ? d.rules : EMPTY.rules, seo: { ...EMPTY.seo, ...(d?.seo || {}) }, products: d?.products || [] }))
            .catch((e) => live && setErr(e));
        return () => { live = false; };
    }, [api, cid, isNew]);

    const set = (patch) => setC((x) => ({ ...x, ...patch }));
    const setProducts = (products) => { set({ products }); setProductsDirty(true); };
    const dragProps = useDragList(c?.products || [], setProducts);
    const conds = c?.rules?.conditions || [];
    const setConds = (conditions) => set({ rules: { ...c.rules, conditions } });

    const save = async () => {
        if (!c.title.trim()) { message.error(t('Title is required')); return; }
        if (c.kind === 'smart' && !conds.length) { message.error(t('Add at least one condition')); return; }
        setSaving(true);
        const body = {
            title: c.title.trim(), handle: c.handle, description_html: c.description_html, image: c.image?.src ? c.image : null,
            sort_order: c.sort_order, published: c.published ? 1 : 0, seo: c.seo,
            ...(isNew ? { kind: c.kind } : {}),
            ...(c.kind === 'smart' ? { rules: c.rules } : {}),
            ...(c.kind === 'manual' && (isNew || productsDirty) ? { product_ids: c.products.map((p) => p.id) } : {}),
        };
        try {
            let d = isNew ? await api.post('/collections', body) : await api.put(`/collections/${cid}`, body);
            if (!isNew && c.kind === 'manual' && productsDirty && !(d?.products)) {
                d = await api.put(`/collections/${cid}/products`, { product_ids: c.products.map((p) => p.id) });
            }
            setProductsDirty(false);
            message.success(t('Saved'));
            if (isNew && d?.id) navigate(sitePath(siteId, `collections/${d.id}`), { replace: true });
            else if (d) setC((x) => ({ ...x, ...d, products: d.products || x.products }));
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };
    const remove = async () => {
        try { await api.del(`/collections/${cid}`); message.success(t('Deleted')); navigate(sitePath(siteId, 'collections')); } catch (e) { toastErr(e); }
    };

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Collection not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'collections'))}>{t('Collections')}</Button>} />;
    if (!c) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const base = siteUrl(site);

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(sitePath(siteId, 'collections'))} />
                <Text strong style={{ fontSize: 18 }}>{isNew ? t('Create collection') : c.title}</Text>
                {!isNew && base && c.handle && <a href={`${base}/collections/${c.handle}`} target="_blank" rel="noopener noreferrer"><ExportOutlined /> {t('View on site')}</a>}
            </Space>
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card style={{ marginBottom: 16 }}>
                        <Form layout="vertical">
                            <Form.Item label={t('Title')} required>
                                <Input value={c.title} placeholder={t('Summer collection')} onChange={(e) => set({ title: e.target.value, ...(handleTouched ? {} : { handle: slugify(e.target.value) }) })} />
                            </Form.Item>
                            <Form.Item label={t('Description')} style={{ marginBottom: 0 }}>
                                <RichEditor value={c.description_html} onChange={(v) => set({ description_html: v })} minHeight={120} />
                            </Form.Item>
                        </Form>
                    </Card>

                    {isNew && (
                        <Card title={t('Collection type')} style={{ marginBottom: 16 }}>
                            <Radio.Group value={c.kind} onChange={(e) => set({ kind: e.target.value })}>
                                <Space direction="vertical">
                                    <Radio value="manual">{t('Manual', { context: 'shop' })} <Text type="secondary">— {t('pick products one by one')}</Text></Radio>
                                    <Radio value="smart">{t('Smart')} <Text type="secondary">— {t('products matching conditions are added automatically')}</Text></Radio>
                                </Space>
                            </Radio.Group>
                        </Card>
                    )}

                    {c.kind === 'smart' && (
                        <Card title={t('Conditions')} style={{ marginBottom: 16 }}>
                            <Space style={{ marginBottom: 12 }}>
                                <Text>{t('Products must match')}</Text>
                                <Radio.Group value={c.rules.match} onChange={(e) => set({ rules: { ...c.rules, match: e.target.value } })}>
                                    <Radio value="all">{t('all conditions')}</Radio>
                                    <Radio value="any">{t('any condition')}</Radio>
                                </Radio.Group>
                            </Space>
                            {conds.map((cd, i) => {
                                const f = FIELDS.find((x) => x.value === cd.field) || FIELDS[0];
                                const numeric = f.money || f.num;
                                const ops = OPS.filter((o) => (numeric ? ['equals', 'not_equals', 'gt', 'lt'].includes(o.value) : !o.num));
                                const upd = (patch) => setConds(conds.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                                return (
                                    <Row gutter={8} key={i} style={{ marginBottom: 8 }}>
                                        <Col xs={24} sm={8}><Select value={cd.field} style={{ width: '100%' }} options={FIELDS.map((x) => ({ value: x.value, label: t(x.label) }))}
                                            onChange={(v) => { const nf = FIELDS.find((x) => x.value === v); upd({ field: v, op: (nf.money || nf.num) ? 'gt' : 'equals', value: '' }); }} /></Col>
                                        <Col xs={24} sm={7}><Select value={cd.op} style={{ width: '100%' }} options={ops.map((x) => ({ value: x.value, label: t(x.label) }))} onChange={(v) => upd({ op: v })} /></Col>
                                        <Col xs={20} sm={8}>
                                            {f.money ? (
                                                <InputNumber style={{ width: '100%' }} addonBefore={currency} value={cd.value === '' ? null : fromMinor(cd.value, currency)} onChange={(v) => upd({ value: v === null ? '' : String(toMinor(v, currency)) })} />
                                            ) : f.num ? (
                                                <InputNumber style={{ width: '100%' }} value={cd.value === '' ? null : Number(cd.value)} onChange={(v) => upd({ value: v === null ? '' : String(v) })} />
                                            ) : <Input value={cd.value} onChange={(e) => upd({ value: e.target.value })} />}
                                        </Col>
                                        <Col xs={4} sm={1}><Button type="text" danger icon={<DeleteOutlined />} onClick={() => setConds(conds.filter((_, j) => j !== i))} /></Col>
                                    </Row>
                                );
                            })}
                            <Button icon={<PlusOutlined />} onClick={() => setConds([...conds, { field: 'tag', op: 'equals', value: '' }])}>{t('Add condition')}</Button>
                        </Card>
                    )}

                    <Card title={t('Products', { context: 'shop' })} style={{ marginBottom: 16 }}
                        extra={(
                            <Space>
                                <Select size="small" value={c.sort_order} onChange={(v) => set({ sort_order: v })} style={{ width: 170 }}
                                    options={SORTS.map((s) => ({ value: s.value, label: `${t('Sort')}: ${t(s.label)}` }))} />
                                {c.kind === 'manual' && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setPicking(true)}>{t('Add products')}</Button>}
                            </Space>
                        )}>
                        {c.kind === 'smart' && !isNew && <Text type="secondary">{t('{{n}} products match', { n: c.products_count ?? c.products.length })}</Text>}
                        {c.products.length ? (
                            <List size="small" dataSource={c.products} renderItem={(p, i) => (
                                <List.Item key={p.id} {...(c.kind === 'manual' && c.sort_order === 'manual' ? dragProps(i) : {})}
                                    actions={c.kind === 'manual' ? [<Button key="x" type="text" size="small" icon={<DeleteOutlined />} onClick={() => setProducts(c.products.filter((x) => x.id !== p.id))} />] : []}>
                                    <Space>
                                        {c.kind === 'manual' && c.sort_order === 'manual' && <HolderOutlined style={{ color: '#aaa' }} />}
                                        <Text type="secondary">{i + 1}.</Text>
                                        <Thumb src={p.image?.src} size={32} />
                                        <a onClick={() => navigate(sitePath(siteId, `products/${p.id}`))}>{p.title}</a>
                                    </Space>
                                </List.Item>
                            )} />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={c.kind === 'smart' ? t('Save to see matching products') : t('No products yet')} />}
                    </Card>

                    <Collapse items={[{
                        key: 'seo', label: t('Search engine listing'),
                        children: (
                            <Form layout="vertical">
                                <Form.Item label={t('Page title')}><Input value={c.seo.title} maxLength={255} onChange={(e) => set({ seo: { ...c.seo, title: e.target.value } })} /></Form.Item>
                                <Form.Item label={t('Meta description')}><Input.TextArea value={c.seo.description} maxLength={500} autoSize={{ minRows: 2 }} onChange={(e) => set({ seo: { ...c.seo, description: e.target.value } })} /></Form.Item>
                                <Form.Item label={t('URL handle')} style={{ marginBottom: 0 }}><Input addonBefore="/collections/" value={c.handle} onChange={(e) => { setHandleTouched(true); set({ handle: slugify(e.target.value) }); }} /></Form.Item>
                            </Form>
                        ),
                    }]} />
                    {!isNew && <CustomFieldsCard owner="collection" oid={cid} style={{ marginTop: 16 }} />}
                    {!isNew && <TranslationsCard type="collection" oid={cid} />}
                </Col>
                <Col xs={24} lg={8}>
                    <Card title={t('Visibility')} style={{ marginBottom: 16 }}>
                        <Space><Switch checked={!!c.published} onChange={(v) => set({ published: v })} /><Text>{c.published ? t('Visible in the store') : t('Hidden')}</Text></Space>
                    </Card>
                    <Card title={t('Image', { context: 'shop' })} style={{ marginBottom: 16 }}>
                        {c.image?.src && <img src={c.image.src} alt="" style={{ width: '100%', borderRadius: 6, marginBottom: 8 }} />}
                        <Input placeholder="https://…" value={c.image?.src || ''} onChange={(e) => set({ image: { ...(c.image || {}), src: e.target.value } })} />
                    </Card>
                    {!isNew && (
                        <Popconfirm title={t('Delete this collection?')} okButtonProps={{ danger: true }} onConfirm={remove}>
                            <Button danger block icon={<DeleteOutlined />}>{t('Delete collection')}</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
            <div style={{ position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 0', marginTop: 8, background: 'var(--ant-color-bg-layout, #f5f5f5)' }}>
                <Button onClick={() => navigate(sitePath(siteId, 'collections'))}>{t('Cancel')}</Button>
                <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
            </div>
            <ProductPicker open={picking} onClose={() => setPicking(false)} initial={c.products}
                onOk={(sel) => {
                    const keep = c.products.filter((p) => sel.some((s) => s.id === p.id));
                    const add = sel.filter((s) => !c.products.some((p) => p.id === s.id));
                    setProducts([...keep, ...add]);
                    setPicking(false);
                }} />
        </div>
    );
};

export default CollectionEdit;
